import { useMemo, useState } from 'react';
import { PhieuNhapChiPhiMuaHang, PhieuNhapChiPhiMuaHangInput } from '@/types/schemas/phieu-nhap-chi-phi-mua-hang.type';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { DeleteDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { getDataTableColumns, getInputTableColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { useToast } from '@/hooks/use-toast';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

export default function PhieuNhapChiPhiMuaHangPage() {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const { toast } = useToast();
  const {
    data: phieuNhapChiPhiMuaHangs,
    isLoading,
    addItem: addPhieuNhapChiPhiMuaHang,
    updateItem: updatePhieuNhapChiPhiMuaHang,
    deleteItem: deletePhieuNhapChiPhiMuaHang,
    refreshData: refreshPhieuNhapChiPhiMuaHangs,
    detail,
    fetchDetail,
    totalItems,
    currentPage,
    handlePageChange,
    sumTotal
  } = useCRUD<PhieuNhapChiPhiMuaHang, PhieuNhapChiPhiMuaHangInput>({
    endpoint: QUERY_KEYS.PHIEU_NHAP_CHI_PHI_MUA_HANG,
    sumField: 't_tien'
  });
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const handleSearch = () => {
    setShowSearchDialog(true);
  };

  const handleSearchClose = () => {
    setShowSearchDialog(false);
  };

  const handleSearchSubmit = (filters: MuaHangRecord) => {
    console.log('Search filters:', filters);
    // TODO: Implement search functionality here
  };

  const handleSubmit = async (data: MuaHangRecord) => {
    try {
      if (formMode === 'add') {
        if (!data.so_ct || data.so_ct.trim() === '') {
          toast({
            description: 'Vui lòng chọn số chứng từ',
            variant: 'destructive'
          });
          return;
        }
        await addPhieuNhapChiPhiMuaHang(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updatePhieuNhapChiPhiMuaHang(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      return;
    }
  };

  const mainColumns = useMemo(
    () => getDataTableColumns(handleViewClick, sumTotal, isLoading),
    [handleViewClick, isLoading, sumTotal]
  );

  const tables = [
    {
      name: 'Tất cả',
      rows: phieuNhapChiPhiMuaHangs,
      columns: mainColumns
    },
    {
      name: 'Chưa ghi sổ',
      rows: phieuNhapChiPhiMuaHangs?.filter((row: PhieuNhapChiPhiMuaHang) => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: phieuNhapChiPhiMuaHangs?.filter((row: PhieuNhapChiPhiMuaHang) => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã ghi sổ',
      rows: phieuNhapChiPhiMuaHangs?.filter((row: PhieuNhapChiPhiMuaHang) => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    }
  ];

  return (
    <div className='flex h-screen w-screen flex-col overflow-hidden'>
      {showSearchDialog && (
        <SearchDialog open={showSearchDialog} onClose={handleSearchClose} onSearch={handleSearchSubmit} />
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={
            detail && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? detail
              : formMode === 'add' && !isCopyMode
                ? undefined
                : undefined
          }
          onSubmit={handleSubmit}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
        />
      )}

      {showDelete && (
        <DeleteDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deletePhieuNhapChiPhiMuaHang}
          clearSelection={clearSelection}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshPhieuNhapChiPhiMuaHangs();
              await fetchDetail(selectedObj?.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          <ResizablePanelGroup direction='vertical' className='flex-1 overflow-hidden'>
            <ResizablePanel defaultSize={55} minSize={20}>
              <div className='h-full w-full overflow-hidden'>
                <ErpDataTableV2
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  tables={tables}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowIndex || undefined}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle className='bg-slate-200/80 hover:bg-slate-300' />

            <ResizablePanel defaultSize={45} minSize={20}>
              <div className='h-full overflow-hidden'>
                <ErpInputTableV2
                  rows={detail?.chi_tiet_phieu_nhaps || []}
                  columns={getInputTableColumns}
                  actionsDisabled={formMode === 'view' || !selectedObj}
                  actionOrder={['export', 'pin']}
                  actions={{ export: () => {}, pin: () => {} }}
                  showIndexColumn
                  height='100%'
                  loading={isLoading && !selectedObj}
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </>
      )}
    </div>
  );
}
